import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownTrayIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { stripHtml as sanitizeStripHtml } from '../../utils/sanitize';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import {
  fetchAllKpiDrilldownTickets,
  fetchKpiDrilldownTickets,
  type KpiDrilldownTicketDto,
} from './kpiDashboardApi';
import { channelLabels, closureCodeLabels } from '../dashboard/types/pulseReport';
import {
  KPI_DASHBOARD_TABLE_TD,
  KPI_DASHBOARD_TABLE_TD_PRIMARY,
  KPI_DASHBOARD_TABLE_TH,
  kpiDashboardTableRowClass,
} from './kpiDashboardTableStyles';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/** Same scope as Change Request KPI volume: changes in range with dashboard filters. */
const CHANGE_DETAILS_DRILL = { kind: 'metric' as const, key: 'changes_volume' };

const CHANGE_CLOSE_LABEL: Record<string, string> = {
  change_success: 'Success',
  change_success_with_impact: 'Success (with impact)',
  change_failed: 'Failed',
  change_failed_with_impact: 'Failed (with impact)',
  change_postponed: 'Cancelled',
};

const PRIORITY_P_LABEL: Record<string, string> = {
  critical_1: 'P1',
  high_2: 'P2',
  moderate_3: 'P3',
  low_4: 'P4',
  planning_5: 'P5',
};

function priorityPLabel(priorityKey: string): string {
  return PRIORITY_P_LABEL[priorityKey] ?? '—';
}

function changeCloseCodeDisplay(code: string | null | undefined): string {
  if (!code) return '—';
  return CHANGE_CLOSE_LABEL[code] ?? closureCodeLabels[code] ?? code;
}

function formatChangeTypeLabel(raw: string): string {
  if (!raw || raw === '—') return '—';
  return raw
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function internalAgingBucket(t: KpiDrilldownTicketDto): string {
  const endMs = t.ticketResolved
    ? new Date(t.ticketResolved).getTime()
    : t.ticketClosed
      ? new Date(t.ticketClosed).getTime()
      : Date.now();
  const startMs = new Date(t.ticketCreated).getTime();
  const hours = Math.max(0, (endMs - startMs) / 3600000);
  if (hours <= 24) return '0-24 Hours';
  if (hours <= 48) return '24-48 Hours';
  if (hours <= 72) return '48-72 Hours';
  if (hours <= 168) return '3-7 Days';
  return '> 7 Days';
}

function openChangeRow(t: KpiDrilldownTicketDto) {
  window.open(`/changes/${t.id}`, '_blank', 'noopener,noreferrer');
}

export interface KpiChangeDetailsTabProps {
  filterValues: Record<string, string | string[]>;
  filterKey: string;
}

const KpiChangeDetailsTab: React.FC<KpiChangeDetailsTabProps> = ({ filterValues, filterKey }) => {
  const { formatDateTime: formatDateTimeBase } = useDateFormatter();
  const formatDateTime = useCallback(
    (value: string | null | undefined) => formatDateTimeBase(value),
    [formatDateTimeBase]
  );

  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [exportingCsv, setExportingCsv] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [filterKey, pageSize]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['kpi-change-details', filterKey, page, pageSize],
    queryFn: () =>
      fetchKpiDrilldownTickets(filterValues, timezoneOffset, CHANGE_DETAILS_DRILL, page, pageSize),
    staleTime: 30_000,
  });

  const total = data?.total ?? 0;

  const handleExportCsv = useCallback(async () => {
    if (total === 0 || exportingCsv) return;
    setExportingCsv(true);
    try {
      const tickets = await fetchAllKpiDrilldownTickets(
        filterValues,
        timezoneOffset,
        CHANGE_DETAILS_DRILL,
        null
      );
      if (tickets.length === 0) {
        toast.error('No change records to export for the current filters.');
        return;
      }
      const headers = [
        'Task',
        'State',
        'Priority Label',
        'Short Description',
        'Actual Start Date',
        'Actual End Date',
        'Assigned To',
        'Assignment Group',
        'Close Code',
        'Closed By',
        'Company',
        'Configuration Item',
        'Type',
        'Contact Type',
        'Created',
        'Created By',
        'Internal Aging Duration',
        'Planned Start Date',
        'Planned End Date',
      ];
      const rows = tickets.map((t) => {
        const desc = t.shortDescription ? sanitizeStripHtml(t.shortDescription).replace(/\s+/g, ' ').trim() : '';
        return [
          t.number,
          t.stateLabel,
          priorityPLabel(t.priority),
          desc,
          formatDateTime(t.actualStartDate),
          formatDateTime(t.actualEndDate),
          t.assignedTo || '-',
          t.assignmentGroup || '-',
          changeCloseCodeDisplay(t.closureCode),
          t.ticketClosedBy || '-',
          t.company || '-',
          t.resource || '-',
          formatChangeTypeLabel(t.typeDisplay),
          t.channel ? channelLabels[t.channel] || t.channel : '-',
          formatDateTime(t.ticketCreated),
          t.createdBy || '-',
          internalAgingBucket(t),
          formatDateTime(t.plannedStartDate),
          formatDateTime(t.plannedEndDate),
        ];
      });
      const csvContent = [
        headers.join(','),
        ...rows.map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ),
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kpi_change_details_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${tickets.length.toLocaleString()} row(s) to CSV.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'CSV export failed.');
    } finally {
      setExportingCsv(false);
    }
  }, [exportingCsv, filterValues, formatDateTime, timezoneOffset, total]);
  const totalPages = data?.totalPages ?? 0;

  const th = (extra = '') => `${KPI_DASHBOARD_TABLE_TH} ${extra}`.trim();
  const td = (extra = '') => `${KPI_DASHBOARD_TABLE_TD} ${extra}`.trim();

  return (
    <div className="relative flex min-h-0 flex-1 flex-col my-2 mx-5">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-l-4 border-slate-100 border-l-indigo-500 bg-gradient-to-r from-slate-50 via-white to-indigo-50/40 py-1.5 pl-[13px] pr-2 sm:py-2 sm:pl-[17px] sm:pr-3">
          <h2 className="min-w-0 text-[11px] font-semibold uppercase tracking-wide text-slate-700 sm:text-xs">
            Change Details
          </h2>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {isFetching ? (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600"
                aria-live="polite"
              >
                <ArrowPathIcon className="h-3 w-3 animate-spin" aria-hidden />
                Updating
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => void handleExportCsv()}
              disabled={total === 0 || exportingCsv || isLoading}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 sm:text-xs disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0 text-slate-600" aria-hidden />
              {exportingCsv ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-slate-500">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
            <p className="mt-3 text-sm">Loading change records…</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="flex shrink-0 items-start gap-2 border-b border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden />
            <span>{error instanceof Error ? error.message : 'Failed to load change details.'}</span>
          </div>
        )}

        {data && !isLoading && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="min-w-max w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Task
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      State
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Priority Label
                    </th>
                    <th className={th('min-w-[280px] max-w-[520px]')} scope="col">
                      Short Description
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Actual Start Date
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Actual End Date
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Assigned To
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Assignment Group
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Close Code
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Closed By
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Company
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Configuration Item
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Type
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Contact Type
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Created
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Created By
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Internal Aging Duration
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Planned Start Date
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Planned End Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.tickets.length === 0 ? (
                    <tr>
                      <td colSpan={19} className="border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500">
                        No change records match the current filters.
                      </td>
                    </tr>
                  ) : (
                    data.tickets.map((t, i) => {
                      const descRaw = t.shortDescription ? sanitizeStripHtml(t.shortDescription) : '';
                      const desc = descRaw.replace(/\s+/g, ' ').trim() || '—';
                      return (
                        <tr
                          key={t.id}
                          className={`cursor-pointer ${kpiDashboardTableRowClass(i)}`}
                          onClick={() => openChangeRow(t)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openChangeRow(t);
                            }
                          }}
                          tabIndex={0}
                          role="link"
                          aria-label={`Open change ${t.number}`}
                        >
                          <td className={`${KPI_DASHBOARD_TABLE_TD_PRIMARY} whitespace-nowrap`}>{t.number}</td>
                          <td className={td('whitespace-nowrap')}>{t.stateLabel}</td>
                          <td className={td('whitespace-nowrap tabular-nums')}>{priorityPLabel(t.priority)}</td>
                          <td className={td('min-w-[280px] max-w-[520px] align-top')}>
                            <div className="line-clamp-3 break-words" title={desc}>
                              {desc}
                            </div>
                          </td>
                          <td className={td('whitespace-nowrap')}>{formatDateTime(t.actualStartDate)}</td>
                          <td className={td('whitespace-nowrap')}>{formatDateTime(t.actualEndDate)}</td>
                          <td className={td('whitespace-nowrap')}>{t.assignedTo || '—'}</td>
                          <td className={td('whitespace-nowrap')}>{t.assignmentGroup || '—'}</td>
                          <td className={td('whitespace-nowrap')}>{changeCloseCodeDisplay(t.closureCode)}</td>
                          <td className={td('whitespace-nowrap')}>{t.ticketClosedBy || '—'}</td>
                          <td className={td('max-w-[200px]')}>
                            <div className="truncate" title={t.company ?? undefined}>
                              {t.company || '—'}
                            </div>
                          </td>
                          <td className={td('max-w-[160px]')}>
                            <div className="truncate" title={t.resource ?? undefined}>
                              {t.resource || '—'}
                            </div>
                          </td>
                          <td className={td('whitespace-nowrap')}>{formatChangeTypeLabel(t.typeDisplay)}</td>
                          <td className={td('whitespace-nowrap')}>
                            {t.channel ? channelLabels[t.channel] || t.channel : '—'}
                          </td>
                          <td className={td('whitespace-nowrap')}>{formatDateTime(t.ticketCreated)}</td>
                          <td className={td('max-w-[200px]')}>
                            <div className="truncate" title={t.createdBy ?? undefined}>
                              {t.createdBy || '—'}
                            </div>
                          </td>
                          <td className={td('whitespace-nowrap')}>{internalAgingBucket(t)}</td>
                          <td className={td('whitespace-nowrap')}>{formatDateTime(t.plannedStartDate)}</td>
                          <td className={td('whitespace-nowrap')}>{formatDateTime(t.plannedEndDate)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 ? (
              <div className="flex shrink-0 flex-col gap-2 border-t border-slate-200 bg-slate-50/50 px-2 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-slate-700">Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                  >
                    {PAGE_SIZE_OPTIONS.map((size, index) => {
                      const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1]! : 0;
                      const isDisabled = previousSize >= total && size > total;
                      return (
                        <option key={size} value={size} disabled={isDisabled}>
                          {size}
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-sm text-slate-700">entries</span>
                </div>
                <div className="text-center text-sm text-slate-700">
                  {isFetching && !isLoading ? (
                    <span className="text-indigo-600">Updating…</span>
                  ) : (
                    <>
                      Showing {Math.min((page - 1) * pageSize + 1, total)} to {Math.min(page * pageSize, total)} of{' '}
                      {total} entries
                    </>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-center gap-1 sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="First page"
                  >
                    &laquo;
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {(() => {
                    const pages: (number | string)[] = [];
                    if (totalPages <= 7) {
                      for (let j = 1; j <= totalPages; j++) pages.push(j);
                    } else if (page <= 3) {
                      pages.push(1, 2, 3, 4, '...', totalPages);
                    } else if (page >= totalPages - 2) {
                      pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                    } else {
                      pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                    }
                    return pages.map((pNum, idx) =>
                      pNum === '...' ? (
                        <span key={`e-${idx}`} className="px-2 py-1 text-sm text-slate-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pNum}
                          type="button"
                          onClick={() => setPage(pNum as number)}
                          className={`rounded-md border px-3 py-1 text-sm ${
                            page === pNum
                              ? 'border-indigo-600 bg-indigo-600 text-white'
                              : 'border-slate-300 bg-white hover:bg-slate-50'
                          }`}
                        >
                          {pNum}
                        </button>
                      )
                    );
                  })()}
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages}
                    className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Last page"
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default KpiChangeDetailsTab;
